import React, { useState } from 'react';
import Button from '../Common/Button';
import './PreflightQA.css';

const ANSWERABLE = new Set(['unnamed_character', 'missing_detail']);

/**
 * The pre-flight Q&A screen (Unit 4 build spec §C1) — renders the
 * scanner's questions and writes answers via B4's propose+confirm
 * endpoint. Not every question type is answerable here:
 * - unnamed_character / missing_detail: a text answer becomes a
 *   confirmed fact.
 * - promise_payoff_unearned: a planning problem, not a fact — points back
 *   to the outline.
 * - missing_dossier: the overridable dossier gate. generate_scene takes
 *   ONE override_dossier_reason for the whole scene (pipeline.py), so
 *   however many characters are missing a dossier, this renders ONE
 *   scene-level "Draft anyway — why?" control, not one per character
 *   (punch-list unit §2.1) — each missing_dossier question still lists
 *   which character it's about, but only the single shared reason is
 *   ever recorded.
 */
export default function PreflightQA({ questions, onAnswer, onOverrideDossier, onBackToOutline, busy }) {
  const dossierQuestions = questions.filter((q) => q.type === 'missing_dossier');
  const otherQuestions = questions.filter((q) => q.type !== 'missing_dossier');

  return (
    <div className="er-preflight">
      <div className="er-preflight__intro">
        <div className="er-preflight__eyebrow">Before drafting</div>
        <h1 className="er-preflight__title">A few things the story hasn't settled yet</h1>
        <p className="er-preflight__lede">
          Answer what you can below — confirming an answer makes it story law for every later scene.
        </p>
      </div>
      <ul className="er-preflight__list">
        {otherQuestions.map((q, i) => (
          <QuestionRow
            key={`${q.type}-${q.character || q.beat_id || q.promise_id || i}`}
            question={q}
            onAnswer={onAnswer}
            onBackToOutline={onBackToOutline}
            busy={busy}
          />
        ))}
      </ul>
      {dossierQuestions.length > 0 && (
        <DossierOverrideGroup questions={dossierQuestions} onOverrideDossier={onOverrideDossier} busy={busy} />
      )}
    </div>
  );
}

function DossierOverrideGroup({ questions, onOverrideDossier, busy }) {
  const [reason, setReason] = useState('');
  const [done, setDone] = useState(false);

  return (
    <div className="er-preflight__dossier-group">
      <ul className="er-preflight__list">
        {questions.map((q, i) => (
          <li key={`missing_dossier-${q.character || i}`} className={`er-preflight__item${done ? ' er-preflight__item--done' : ''}`}>
            {done && <span className="er-preflight__done-check" aria-hidden="true">✓</span>}
            <p className="er-preflight__question">{q.question}</p>
          </li>
        ))}
      </ul>
      {!done && (
        <div className="er-preflight__item">
          <label className="er-preflight__field">
            <span className="er-preflight__field-label">Draft anyway — why? (recorded on the receipt)</span>
            <input
              className="er-preflight__input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Confirming the dossier after this draft"
            />
          </label>
          <Button
            variant="secondary" size="small" disabled={!reason.trim() || busy}
            onClick={() => { onOverrideDossier(reason.trim()); setDone(true); }}
          >
            Override — draft anyway
          </Button>
        </div>
      )}
    </div>
  );
}

function QuestionRow({ question, onAnswer, onBackToOutline, busy }) {
  const [value, setValue] = useState('');
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <li className="er-preflight__item er-preflight__item--done">
        <span className="er-preflight__done-check" aria-hidden="true">✓</span>
        <span>{question.question}</span>
      </li>
    );
  }

  if (question.type === 'promise_payoff_unearned') {
    return (
      <li className="er-preflight__item">
        <p className="er-preflight__question">{question.question}</p>
        <button type="button" className="er-preflight__link" onClick={onBackToOutline}>
          Go to the outline →
        </button>
      </li>
    );
  }

  if (!ANSWERABLE.has(question.type)) {
    return (
      <li className="er-preflight__item">
        <p className="er-preflight__question">{question.question}</p>
      </li>
    );
  }

  const submit = async () => {
    if (!value.trim()) return;
    await onAnswer({ type: question.type, character: question.character, answer: value.trim() });
    setDone(true);
  };

  return (
    <li className="er-preflight__item">
      <p className="er-preflight__question">{question.question}</p>
      <div className="er-preflight__answer-row">
        <input
          className="er-preflight__input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="Your answer"
          disabled={busy}
        />
        <Button variant="secondary" size="small" disabled={!value.trim() || busy} onClick={submit}>
          Confirm
        </Button>
      </div>
    </li>
  );
}
